import { Component, ReactNode } from 'react';
import { AlertTriangle, RefreshCw } from 'lucide-react';

// Catches render errors in any descendant page so a single bad payload
// (e.g. an unexpected message content shape) doesn't blank the entire
// admin app. Resets when the route key changes.
interface Props {
  children: ReactNode;
  resetKey?: string;
}

interface State {
  error: Error | null;
}

export default class ErrorBoundary extends Component<Props, State> {
  state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  componentDidCatch(error: Error, info: { componentStack?: string }) {
    console.error('Admin UI render error:', error, info.componentStack);
  }

  componentDidUpdate(prevProps: Props) {
    if (prevProps.resetKey !== this.props.resetKey && this.state.error) {
      this.setState({ error: null });
    }
  }

  render() {
    const { error } = this.state;
    if (!error) return this.props.children;

    return (
      <div className="card-glow rounded-2xl p-8 max-w-2xl mx-auto mt-12">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 rounded-xl bg-amber-500/10 flex items-center justify-center">
            <AlertTriangle className="w-5 h-5 text-amber-400" />
          </div>
          <div>
            <h2 className="text-lg font-semibold">This page hit a render error</h2>
            <p className="text-sm text-muted-foreground">
              The rest of the admin UI is still usable.
            </p>
          </div>
        </div>

        <div className="rounded-xl bg-background/40 border border-border p-4 mb-4 font-mono text-xs text-amber-200 overflow-x-auto">
          {error.message || String(error)}
        </div>

        <button
          onClick={() => this.setState({ error: null })}
          className="btn-secondary px-4 py-2 rounded-xl flex items-center gap-2 text-sm"
        >
          <RefreshCw className="w-4 h-4" />
          Try again
        </button>
      </div>
    );
  }
}
